import { useCallback, useState } from 'react';
import { ACTIONS, CallBackProps, EVENTS, STATUS, Step } from 'react-joyride';

interface UseProfileTourProps {
  steps: Step[];
  autoStart?: boolean;
}

export const useProfileTour = ({
  steps,
  autoStart = false,
}: UseProfileTourProps) => {
  const [run, setRun] = useState(autoStart);
  const [stepIndex, setStepIndex] = useState(0);
  const [tourCompleted, setTourCompleted] = useState(false);

  const handleJoyrideCallback = useCallback(
    (data: CallBackProps) => {
      const { status, type, action, index } = data;

      // Handle tour end states
      if (status === STATUS.FINISHED) {
        setRun(false);
        setStepIndex(0);
        setTourCompleted(true);
        return;
      }

      if (status === STATUS.SKIPPED) {
        setRun(false);
        setStepIndex(0);
        return;
      }

      // Handle step navigation
      if (type === EVENTS.STEP_AFTER) {
        if (action === ACTIONS.NEXT) {
          if (index < steps.length - 1) {
            setStepIndex(index + 1);
          } else {
            // Tour is complete
            setRun(false);
            setStepIndex(0);
            setTourCompleted(true);
          }
        } else if (action === ACTIONS.PREV) {
          setStepIndex(Math.max(0, index - 1));
        }
      }

      // Handle tour close/skip
      if (type === EVENTS.TARGET_NOT_FOUND || action === ACTIONS.CLOSE) {
        setRun(false);
        setStepIndex(0);
      }
    },
    [steps.length]
  );

  const startTour = useCallback(() => {
    setRun(true);
    setStepIndex(0);
    setTourCompleted(false);
  }, []);

  const stopTour = useCallback(() => {
    setRun(false);
    setStepIndex(0);
  }, []);

  const resetTour = useCallback(() => {
    setRun(false);
    setStepIndex(0);
    setTourCompleted(false);
  }, []);

  const goToStep = useCallback(
    (step: number) => {
      if (step >= 0 && step < steps.length) {
        setStepIndex(step);
      }
    },
    [steps.length]
  );

  return {
    run,
    stepIndex,
    tourCompleted,
    handleJoyrideCallback,
    startTour,
    stopTour,
    resetTour,
    goToStep,
  };
};
